import React from 'react';
import { Clock, LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, isToday, isYesterday } from 'date-fns';

interface Activity {
  id?: string;
  type: string;
  title?: string;
  details: string[];
  date: Date | string;
  icon?: LucideIcon;
  status?: 'completed' | 'in-progress' | 'pending';
}

interface ActivityListProps {
  activities: Activity[];
  title?: string;
  showDates?: boolean;
  variant?: 'default' | 'minimal' | 'compact';
  maxItems?: number;
  className?: string;
  onActivityClick?: (activity: Activity) => void;
}

export const ActivityList: React.FC<ActivityListProps> = ({
  activities,
  title = "Recent Activities",
  showDates = true,
  variant = 'default',
  maxItems,
  className,
  onActivityClick
}) => {
  const displayedActivities = maxItems ? activities.slice(0, maxItems) : activities;

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isToday(dateObj)) return 'Today';
    if (isYesterday(dateObj)) return 'Yesterday';
    return format(dateObj, 'MMM d');
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'in-progress':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'pending':
        return 'bg-orange-50 border-orange-200 text-orange-800';
      default:
        return '';
    }
  };

  const getItemStyles = () => {
    switch (variant) {
      case 'minimal':
        return 'py-2 border-b border-border last:border-0';
      case 'compact':
        return 'p-2 bg-muted/30 rounded border';
      default:
        return 'p-3 bg-white/40 backdrop-blur-sm rounded-lg border border-white/20';
    }
  };

  if (!activities.length) {
    return (
      <div className={cn("text-center py-8 text-muted-foreground", className)}>
        <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No recent activities</p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      {title && (
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Clock className="h-4 w-4" />
          {title}
        </div>
      )}
      
      <div className="space-y-2">
        {displayedActivities.map((activity, index) => (
          <div 
            key={activity.id || index}
            className={cn(
              "transition-all duration-200",
              getItemStyles(),
              activity.status && getStatusColor(activity.status),
              onActivityClick && "cursor-pointer hover:shadow-sm",
            )}
            onClick={() => onActivityClick?.(activity)}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                {activity.icon && (
                  <div className="p-1 rounded bg-primary/10 text-primary mt-0.5 flex-shrink-0">
                    <activity.icon className="h-3 w-3" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium capitalize">
                      {activity.title || activity.type}
                    </span>
                    {activity.status && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-current/10 text-current">
                        {activity.status}
                      </span>
                    )}
                  </div>
                  {activity.details.length > 0 && (
                    <div className="space-y-1">
                      {activity.details.map((detail, detailIndex) => (
                        <div key={detailIndex} className="text-xs text-muted-foreground">
                          • {detail}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {showDates && (
                <div className="text-xs text-muted-foreground flex-shrink-0">
                  {formatDate(activity.date)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {maxItems && activities.length > maxItems && (
        <div className="text-center pt-2">
          <span className="text-xs text-muted-foreground">
            Showing {maxItems} of {activities.length} activities
          </span>
        </div>
      )}
    </div>
  );
};